// =====================================================================
// src/lib/indicadores.js — os indicadores operacionais da Auditoria, sem DOM
//
// Hugo, 24/09/2026: "na aba auditoria, quero gráficos com visual para estas
// funções, de fácil identificação: tempo médio de liberação do quarto
// (check-out), início de limpeza e tempo total de limpeza / tempo médio de
// resposta de chamados / tempo médio de check-in na recepção, com e sem
// pré-check-in. Gráfico padrão: o do dia. Opção de selecionar período."
//
// O banco (0060, `indicadores_operacionais`) devolve formato longo — uma
// linha por (dia, métrica) com a média em minutos e o N. Este arquivo:
//   - junta o período num número só por métrica (média PONDERADA pelo N:
//     um dia com 1 limpeza não pesa o mesmo que um com 40);
//   - monta a série por dia, com buraco (null) onde não houve dado — dia
//     sem limpeza não é "zero minuto";
//   - escreve os minutos do jeito que a recepção fala ("2h 20min");
//   - monta a configuração do Chart.js: DIA = barras, uma por métrica;
//     PERÍODO = linhas, um ponto por dia.
//
// O desenho dos cartões é o `_gtCartao` da Gestão de Tempo do MdM
// (index.html 20185): rótulo cinza pequeno, número grande na cor.
// =====================================================================

export const METRICAS = Object.freeze({
  espera_limpeza:  { rotulo: 'Check-out → início da limpeza', curto: 'Espera',      cor: '#d97706' },
  limpeza:         { rotulo: 'Tempo de limpeza',              curto: 'Limpeza',     cor: '#1B3A7A' },
  inspecao:        { rotulo: 'Fim da limpeza → liberação',    curto: 'Inspeção',    cor: '#7c3aed' },
  liberacao_total: { rotulo: 'Total: check-out → liberado',   curto: 'Total',       cor: '#059669' },
  chamado:         { rotulo: 'Abertura → conclusão',          curto: 'Chamado',     cor: '#dc2626' },
  checkin_com_pre: { rotulo: 'Com pré-check-in',              curto: 'Com pré',     cor: '#059669' },
  checkin_sem_pre: { rotulo: 'Sem pré-check-in',              curto: 'Sem pré',     cor: '#d97706' },
});

export const GRUPOS = Object.freeze([
  { chave: 'quarto',  titulo: 'Liberação do quarto',   explica: 'Do check-out ao quarto liberado pela inspeção.',
    metricas: ['espera_limpeza', 'limpeza', 'inspecao', 'liberacao_total'] },
  { chave: 'chamado', titulo: 'Resposta aos chamados', explica: 'Da abertura do chamado à conclusão pela manutenção.',
    metricas: ['chamado'] },
  { chave: 'checkin', titulo: 'Check-in na recepção',  explica: 'Da abertura da reserva na tela de Check-in à chegada do primeiro hóspede.',
    metricas: ['checkin_com_pre', 'checkin_sem_pre'] },
]);

// null e '' viram null, não zero: "sem dado" não é "zero minuto"
const num = v => { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

// { metrica: { media, n } } do período inteiro. Métrica sem dado: media null, n 0.
export function resumo(linhas) {
  const soma = {}, qtd = {};
  for (const l of linhas || []) {
    const m = num(l.media_min), n = Number(l.n) || 0;
    if (m === null || n <= 0) continue;
    soma[l.metrica] = (soma[l.metrica] || 0) + m * n;
    qtd[l.metrica] = (qtd[l.metrica] || 0) + n;
  }
  const r = {};
  for (const k of Object.keys(METRICAS)) r[k] = qtd[k] ? { media: Math.round((soma[k] / qtd[k]) * 10) / 10, n: qtd[k] } : { media: null, n: 0 };
  return r;
}

// os dias do período, inclusive, em ISO
export function diasDoPeriodo(de, ate) {
  const d = [];
  const [a, m, dd] = String(de).split('-').map(Number);
  const fim = String(ate);
  for (let t = Date.UTC(a, m - 1, dd); ; t += 86400000) {
    const iso = new Date(t).toISOString().slice(0, 10);
    if (iso > fim) break;
    d.push(iso);
    if (d.length > 400) break;   // o banco já recusa mais de um ano
  }
  return d;
}

// a série de uma métrica, um valor por dia do período (null = sem dado)
export function seriePorDia(linhas, de, ate, metrica) {
  const por = new Map();
  for (const l of linhas || []) if (l.metrica === metrica) por.set(String(l.dia).slice(0, 10), num(l.media_min));
  return diasDoPeriodo(de, ate).map(d => (por.has(d) ? por.get(d) : null));
}

// 45 → "45 min" · 140 → "2h 20min" · 60 → "1h 00min" · null → "—"
export function fmtMin(min) {
  const v = num(min);
  if (v === null) return '—';
  const t = Math.round(v);
  if (t < 60) return `${t} min`;
  return `${Math.floor(t / 60)}h ${String(t % 60).padStart(2, '0')}min`;
}

export function rotuloDoN(n) {
  if (!n) return 'sem registro';
  return `${n} registro${n === 1 ? '' : 's'}`;
}

const diaCurto = iso => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
export const umDia = (de, ate) => de === ate;

// Chart.js: o DIA é barra (uma por métrica — comparar as etapas é a
// pergunta); o PERÍODO é linha (a tendência dia a dia é a pergunta).
export function configDoGrafico(grupo, linhas, de, ate) {
  const g = typeof grupo === 'string' ? GRUPOS.find(x => x.chave === grupo) : grupo;
  const eixoY = { beginAtZero: true, grace: '10%', ticks: { maxTicksLimit: 6, callback: v => fmtMin(v) } };
  if (umDia(de, ate)) {
    const r = resumo(linhas);
    return {
      type: 'bar',
      data: {
        labels: g.metricas.map(k => METRICAS[k].curto),
        datasets: [{ label: g.titulo, data: g.metricas.map(k => r[k].media), backgroundColor: g.metricas.map(k => METRICAS[k].cor), borderRadius: 6, maxBarThickness: 64 }],
      },
      options: { responsive: true, maintainAspectRatio: false, animation: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => `${ctx.label}: ${fmtMin(ctx.parsed.y)}` } } }, scales: { x: { grid: { display: false } }, y: eixoY } },
    };
  }
  return {
    type: 'line',
    data: {
      labels: diasDoPeriodo(de, ate).map(diaCurto),
      datasets: g.metricas.map(k => ({ label: METRICAS[k].curto, data: seriePorDia(linhas, de, ate, k),
        borderColor: METRICAS[k].cor, backgroundColor: METRICAS[k].cor + '33', borderWidth: 2, tension: 0.3, spanGaps: true, pointRadius: 3 })),
    },
    options: { responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { position: 'top' }, tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${fmtMin(ctx.parsed.y)}` } } }, scales: { x: { grid: { display: false } }, y: eixoY } },
  };
}
